<template lang="pug">
form.form.has-toggler.has-bottom(
  :class="classList",
  @submit.prevent="register",
  @input="resetError",
  novalidate
)
  .form__top
    .form__header
      h1.form__title {{ $l10n('REGISTRATION') }}
      .form__toggler
        Toggler(v-model="tabs.current", :list="tabs.list")

    .form__body
      .form__async-errors(v-if="asyncError")
        .form__error {{ asyncError }}

      label.form__control(
        v-if="isArtist",
        :class="{ 'has-error': $v.user.title.$invalid }"
      )
        span.form__field
          Input(
            v-model="user.title",
            :set-focus="true",
            :placeholder="$l10n('YOUR_FULLNAME')",
            name="title",
            type="text"
          )
        span.form__errors(v-if="$v.user.title.$invalid")
          span.form__error(v-if="!$v.user.title.required") {{ $l10n('VALIDATION_REQUIRED_FIELD') }}

      label.form__control(
        v-if="isArtist",
        :class="{ 'has-error': $v.user.tel.$invalid }"
      )
        span.form__field
          Input(
            v-model="user.tel",
            :placeholder="$l10n('YOUR_TEL')",
            :hint="$l10n('FOR_DATA_CONFIRM')",
            :title="$l10n('PHONE_NUMBER')",
            name="tel",
            type="tel"
          )
        span.form__errors(v-if="$v.user.tel.$invalid")
          span.form__error(v-if="!$v.user.tel.required") {{ $l10n('VALIDATION_REQUIRED_FIELD') }}

      label.form__control(:class="{ 'has-error': $v.user.email.$invalid }")
        span.form__field
          Input(
            v-model="user.email",
            :set-focus="true",
            :placeholder="$l10n('EMAIL')",
            :key="tabs.current",
            name="email",
            type="email"
          )
        span.form__errors(v-if="$v.user.email.$invalid")
          span.form__error(v-if="!$v.user.email.required") {{ $l10n('VALIDATION_REQUIRED_FIELD') }}
          span.form__error(v-if="!$v.user.email.email") {{ $l10n('VALIDATION_NOTVALID_EMAIL') }}

      label.form__control(:class="{ 'has-error': $v.user.password.$invalid }")
        span.form__field
          Input(
            v-model="user.password",
            :placeholder="$l10n('PASSWORD')",
            name="password",
            type="password"
          )
        span.form__errors(v-if="$v.user.password.$invalid")
          span.form__error(v-if="!$v.user.password.required") {{ $l10n('VALIDATION_REQUIRED_FIELD') }}
          span.form__error(v-if="!$v.user.password.minLength") {{ $l10n('VALIDATION_PASSWORD_MIN_LENGHT', { length: $v.user.password.$params.minLength.min }) }}

      label.form__control(:class="{ 'has-error': $v.user.confirm.$invalid }")
        span.form__field
          Input(
            v-model="user.confirm",
            :placeholder="$l10n('REPEAT_PASSWORD')",
            name="confirm",
            type="password"
          )
        span.form__errors(v-if="$v.user.confirm.$invalid")
          span.form__error(v-if="!$v.user.confirm.required") {{ $l10n('VALIDATION_REQUIRED_FIELD') }}
          span.form__error(v-if="!$v.user.confirm.minLength") {{ $l10n('VALIDATION_PASSWORD_MIN_LENGHT', { length: $v.user.confirm.$params.minLength.min }) }}
          span.form__error(v-if="!$v.user.confirm.sameAsPassword") {{ $l10n('PASSWORDS_MISMATCH') }}

    .form__footer
      .form__button
        Button(:label="$l10n('REGISTRATION')", class-list="is-wide")
      .form__agreement
        .form__checkbox
          Checkbox(
            name="agreement",
            v-model="user.agreement",
            :label="$l10n('AGREEMENT_CHECKBOX_TEXT', { url: isArtist ? '/pages/artist-agreement' : '/pages/agreement' })"
          )
        span.form__errors(v-if="$v.user.agreement.$invalid")
          span.form__error(v-if="!$v.user.agreement.required") {{ $l10n('VALIDATION_REQUIRED_AGREEMENT') }}

  .form__bottom
    p.form__text {{ $l10n('ALREADY_REGISTERED') }}
    .form__button
      Button(
        :label="$l10n('ENTRY')",
        @click="openLoginModal",
        class-list="is-wide is-bordered is-login"
      )
</template>
<script>
import Toggler from "@/components/Toggler/Toggler";
import Button from "@/components/Button/Button";
import Input from "@/components/Input/Input";
import Checkbox from "@/components/Checkbox/Checkbox";

import { required, minLength, email, sameAs } from "vuelidate/lib/validators";
import { mapActions } from "vuex";

const phone = value => {
  return /^\+7 \([0-9]{3}\) [0-9]{3}-[0-9]{2}-[0-9]{2}$/.test(value);
};

export default {
  name: "RegisterFormTemplate",
  data() {
    return {
      loading: false,
      asyncError: null,
      tabs: {
        current: "user",
        list: [
          { label: this.$l10n("I_LISTENER"), id: "user" },
          { label: this.$l10n("I_ARTIST"), id: "artist" }
        ]
      },
      user: {
        title: "",
        tel: "",
        email: "",
        password: "",
        confirm: "",
        agreement: false
      }
    };
  },
  validations() {
    const base = {
      email: {
        email,
        required
      },
      password: {
        required,
        minLength: minLength(8)
      },
      confirm: {
        required,
        sameAsPassword: sameAs("password"),
        minLength: minLength(8)
      },
      agreement: {
        checked: sameAs(() => true)
      }
    };

    const artist = {
      title: {
        required
      },
      tel: {
        required
      }
    };

    return this.isArtist
      ? { user: { ...artist, ...base } }
      : { user: { ...base } };
  },
  computed: {
    isArtist() {
      return this.tabs.current === "artist";
    },
    classList() {
      return {
        "has-error": Boolean(this.asyncError),
        "is-dirty": this.$v.$dirty,
        "is-loading": this.loading
      };
    }
  },
  components: { Button, Input, Toggler, Checkbox },
  methods: {
    ...mapActions({
      userRegister: "user/register",
      openModal: "modal/open"
    }),
    resetError() {
      this.$v.$reset();
      this.asyncError = null;
    },
    async register() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.loading = true;

        const payload = this.isArtist
          ? {
              type: "artist",
              user: {
                title: this.user.title,
                phone: this.user.tel,
                email: this.user.email.toLowerCase(),
                password: this.user.password,
                password_confirmation: this.user.confirm
              }
            }
          : {
              type: "user",
              user: {
                email: this.user.email.toLowerCase(),
                password: this.user.password,
                password_confirmation: this.user.confirm
              }
            };

        const { error } = await this.userRegister(payload);

        this.loading = false;

        if (error) {
          this.asyncError = error;
        }
      }
    },
    openLoginModal() {
      this.openModal("login");
    }
  }
};
</script>
